import type { ApolloError } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client';
import React, { useLayoutEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import authTokenUtil from '../../account/authTokenUtil';
import getRedirectQueryString from '../../account/getRedirectQueryString';
import localLogout from '../../account/localLogout';
import { SessionActions } from '../../account/sessionSlice';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import useSlug from '../../utils/router/useSlug';
import { publicPostPath } from '../../utils/url/postPath';
import { publicTopicPath } from '../../utils/url/topicPath';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';

const CURRENT_SESSION_QUERY = gql(`
  query currentSession {
    currentSession {
      ...startSession_session
    }
  }
`);

const SET_ACTIVE_MUTATION = gql(`
  mutation setActive {
    setActive
  }
`);

const PUBLIC_POST_REDIRECT = gql(`
  query PublicPostRedirect($id: ID!) {
    publicPost(id: $id) {
      id
      ...publicPostPath_post
    }
  }
`);

const PUBLIC_TOPIC_REDIRECT = gql(`
  query PublicTopicRedirect($id: ID!) {
    publicTopic(id: $id) {
      id
      ...publicTopicPath_topic
    }
  }
`);

interface EnsureLoggedInProps {
  children?: JSX.Element;
}

const LogOut = () => {
  const loginUrl = `/login${getRedirectQueryString()}`;
  if (authTokenUtil.get()) {
    localLogout();
    return null;
  }
  return <Navigate to={loginUrl} replace />;
};

const TryPublicPost = () => {
  const { hash } = useLocation();
  const [id] = useSlug('postSlug');
  const { data, loading } = useQuery(PUBLIC_POST_REDIRECT, {
    variables: { id },
  });

  if (loading) return null;
  if (data?.publicPost?.id) {
    return (
      <Navigate
        to={publicPostPath(
          data.publicPost,
          hash ? hash.replace('#', '') : undefined,
        )}
        replace
      />
    );
  }

  return <LogOut />;
};

const TryPublicTopic = () => {
  const [id] = useSlug('topicSlug');
  const { data, loading } = useQuery(PUBLIC_TOPIC_REDIRECT, {
    variables: { id },
  });

  if (loading) return null;
  if (data?.publicTopic?.id) {
    return <Navigate to={publicTopicPath(data.publicTopic)} replace />;
  }

  return <LogOut />;
};

const TryPublicContentRedirect = () => {
  const location = useLocation();
  const params = useParams();

  if (location.pathname.startsWith('/posts/') && params.postSlug) {
    return <TryPublicPost />;
  }

  if (location.pathname.startsWith('/topics/') && params.topicSlug) {
    return <TryPublicTopic />;
  }

  return <LogOut />;
};

const isAuthError = (error: ApolloError) =>
  error.networkError &&
  'statusCode' in error.networkError &&
  error.networkError.statusCode === 401;

const TryLogIn = () => {
  const token = authTokenUtil.get();

  const { data, loading, error } = useQuery(CURRENT_SESSION_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !token,
  });
  const currentSession = data?.currentSession;

  // TODO: Active mutation
  const [setActive] = useMutation(SET_ACTIVE_MUTATION);

  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (currentSession) {
      dispatch(SessionActions.startSession(currentSession));
      setActive();
    }
  }, [currentSession, dispatch, setActive]);

  return loading || currentSession || (error && !isAuthError(error)) ? null : (
    <TryPublicContentRedirect />
  );
};

const EnsureLoggedIn = ({ children }: EnsureLoggedInProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  return currentUser.id && currentUser.type !== UserType.Visitor ? (
    children || null
  ) : (
    <TryLogIn />
  );
};

export default EnsureLoggedIn;
